import type { ReactNode } from 'react';
import { PageLoader } from '@setvi/shared/components';
import { CompanyProductUserType } from '@setvi/shared/services/react-query/query/user/types';
import { Box } from '@material-ui/core';
import { Customer } from 'Services/Mongo/Types';
import {
  CatalogConfig,
  PriceGroup,
  Product,
  ProductDetailsTabsIds
} from '@setvi/shared/interfaces';
import { PricingType } from 'pages/products/api';
import { DetailsContent } from './content';
import { useProductDetails } from './hooks';
import { useSalesKitStore } from './store';
import { useAskAIStorage } from './content/components/ask-ai/store';
import { ProductDetailsEmptyState } from './empty-state';
import type {
  ProductDetailsExtraTab,
  ProductDetailsExtraTabId,
  ProductDetailsTabId
} from './content/components/tabs';

export type {
  ProductDetailsExtraTab,
  ProductDetailsExtraTabId,
  ProductDetailsTabId
};

export interface DetailsDialogProps {
  selectedProduct: Partial<Product>;
  selectedProducts?: Product[];
  selectedCustomerId?: Customer['_id'];
  selectedCategoryId?: string;
  selectedPriceGroup?: PriceGroup;
  defaultOpenedTab?: ProductDetailsTabId;
  /** caller-owned tabs shown after the configured ones */
  extraTabs?: ProductDetailsExtraTab[];
  /** caller-owned controls placed just before the close button */
  headerActions?: ReactNode;
  /** hide the rep's "Feedback (n)" button when a tab already lists the reports */
  hideFeedbackList?: boolean;
  userType?: CompanyProductUserType;
  readOnly?: boolean;
  canSelectProducts?: boolean;
  selectionType?: 'none' | 'checkbox' | 'radio';
  hideProductPrice?: boolean;
  priceLoading?: boolean;
  pricingType?: PricingType;
  deliveryDate?: string;
  configOverride?: CatalogConfig;
  orderHistoryValues?: {
    accountId: string;
    addressId?: string;
    infoMessage?: string;
    customerName?: string;
    showShipToButton?: boolean;
  };
  disableStockRefresh?: boolean;
  onClose: () => void;
  handleProductSelect?: (isChecked: boolean, product: Product[]) => void;
  updateProduct?: (product: Product, newValues: Partial<Product>) => void;
  captureResourceAnalytics?: (additionalValues?: {
    [key: string]: string | number;
  }) => void;
  openProductDetails?: (
    product: any,
    defaultOpenedTab?: ProductDetailsTabsIds
  ) => void;
  handleEmail?: () => void;
  handleSelected?: () => void;
  handleCloseActionBar?: () => void;
}

export const ProductDetails = ({
  selectedProduct,
  selectedProducts,
  selectedCustomerId,
  selectedCategoryId,
  selectedPriceGroup,
  orderHistoryValues,
  defaultOpenedTab,
  extraTabs,
  headerActions,
  hideFeedbackList,
  userType,
  readOnly = false,
  canSelectProducts,
  selectionType,
  priceLoading,
  hideProductPrice = false,
  pricingType,
  deliveryDate,
  configOverride,
  disableStockRefresh,
  handleProductSelect,
  onClose,
  updateProduct,
  captureResourceAnalytics,
  openProductDetails,
  handleEmail,
  handleSelected,
  handleCloseActionBar
}: DetailsDialogProps) => {
  const {
    product,
    customer,
    isLoading,

    updateDialogProduct
  } = useProductDetails({
    selectedProduct,
    selectedCustomerId,
    userType
  });

  const { resetSalesKitStore } = useSalesKitStore();
  const { reset } = useAskAIStorage();

  const triggerClose = () => {
    onClose();
    resetSalesKitStore();
    reset();
  };

  if (!selectedProduct) return null;

  return (
    <Box
      tabIndex={0}
      overflow="hidden"
      display="flex"
      flex={1}
      style={{ background: '#F8F9FA' }}>
      {isLoading ? (
        <PageLoader />
      ) : (
        <>
          {product ? (
            <DetailsContent
              product={product}
              selectedCustomer={customer}
              selectedPriceGroup={selectedPriceGroup}
              readOnly={readOnly}
              defaultOpenedTab={defaultOpenedTab}
              extraTabs={extraTabs}
              headerActions={headerActions}
              hideFeedbackList={hideFeedbackList}
              selectedProducts={selectedProducts}
              canSelectProducts={canSelectProducts}
              selectedCategoryId={selectedCategoryId}
              selectionType={selectionType}
              hideProductPrice={hideProductPrice}
              configOverride={configOverride}
              orderHistoryValues={orderHistoryValues}
              priceLoading={priceLoading}
              pricingType={pricingType}
              deliveryDate={deliveryDate}
              handleProductSelect={handleProductSelect}
              disableStockRefresh={disableStockRefresh}
              captureResourceAnalytics={captureResourceAnalytics}
              updateProduct={(prod, newValues) => {
                updateDialogProduct(newValues);
                updateProduct?.(prod, newValues);
              }}
              openProductDetails={openProductDetails}
              closeDialog={triggerClose}
              handleEmail={handleEmail}
              handleSelected={handleSelected}
              handleCloseActionBar={handleCloseActionBar}
            />
          ) : (
            <ProductDetailsEmptyState onClose={onClose} />
          )}
        </>
      )}
    </Box>
  );
};
